import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import * as marked from 'marked';

import { AccountService } from '../../../../../../services/account/account.service';
import { EventTypeService } from '../../../../../../services/event-type/event-type.service';

import { EventQuestion, EventType } from '../../../../../../models/event-type';
import { DomSanitizer } from '@angular/platform-browser';
import { CustomCustomerField } from '../../../../../../models/organization-config';
import { OrganizationConfigService } from '../../../../../../services/organization-config/organization-config.service';
import { EventQuestionScope, EventQuestionType } from '../../../../../../models/event-type-enums';
import { CustomCustomerFieldType } from '../../../../../../models/organization-config-enums';

@Component({
  selector: 'app-event-type-questions',
  templateUrl: './event-type-questions.component.html',
  styleUrls: ['./event-type-questions.component.css']
})
export class EventTypeQuestionsComponent implements OnInit {

  readonly SUBMIT_TYPE_GO_BACK = 'goBack';
  readonly SUBMIT_TYPE_CONTINUE = 'continue';

  public eventType: EventType = null;
  public participantQuestions: EventQuestion[];
  public bookingCustomerQuestions: EventQuestion[];
  // Whether each question is currently in edit mode.
  public questionInEditMode: {[key: number]: boolean} = {};
  public submitType = this.SUBMIT_TYPE_CONTINUE;

  // Mappings from question IDs to feedback for that question.
  public eventQuestionsErrorMessage: {[key: number]: string} = {};
  public eventQuestionsErrors: {[key: number]: any} = {};

  public customCustomerFields: CustomCustomerField[] = null;

  public loadingEventType = true;
  public loadingEventQuestionsList = true;
  // A mapping from question IDs to whether that question is currently loading.
  public loadingEventQuestions: {[key: number]: boolean} = {};
  public loadingCustomCustomerFields = true;

  public eventTypeNotFound = false;
  public eventQuestionsNotFound = false;
  public serverError: number = null;

  // Create instance references to enums to they may be called in the template.
  public readonly EventQuestionScope = EventQuestionScope;
  public readonly EventQuestionType = EventQuestionType;
  public readonly CustomCustomerFieldType = CustomCustomerFieldType;

  /**
   * Get a random negative ID value, used to represent an object which has not
   * yet been saved to the API.
   */
  private static getRandomNegativeId() {
    return Math.floor(
      -1 - (Math.random() * Math.pow(2, 30))
    );
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private accountService: AccountService,
    private eventTypeService: EventTypeService,
    private orgConfigService: OrganizationConfigService,
    private domSanitizer: DomSanitizer
  ) {}

  ngOnInit() {
    const routeParams = this.route.snapshot.params;
    const eventTypeIdInt = parseInt(routeParams.id, 10);
    if (isNaN(eventTypeIdInt)) {
      this.eventTypeNotFound = true;
      return;
    }

    this.loadEventType(eventTypeIdInt);
    this.loadEventQuestions(eventTypeIdInt);
    this.loadCustomCustomerFields();
  }

  /**
   * Whether there are any outstanding API requests in progress.
   */
  public isLoading() {
    return (
      this.loadingEventType ||
      this.loadingEventQuestionsList ||
      !Object.keys(this.loadingEventQuestions).every((k) => this.loadingEventQuestions[k] === false)
    );
  }

  /**
   * Add a new question.
   */
  public addNewQuestion(scope: EventQuestionScope) {
    // Choose a random negative question ID
    const newQuestionId = EventTypeQuestionsComponent.getRandomNegativeId();

    const questions = this.getQuestionListForScope(scope);

    questions.push({
      id: newQuestionId,
      url: '',
      scope,
      question_type: EventQuestionType.singleLineText,
      name: '',
      description: '',
      is_required: false,
      all_options_required: false,
      options: '',
      custom_customer_field_id: null,
      custom_customer_field_name: null,
      custom_customer_field_type: null,
      custom_customer_field_options: null,
      order_number: questions.length,
      created_at: '',
      updated_at: ''
    });
    this.questionInEditMode[newQuestionId] = true;
    this.loadingEventQuestions[newQuestionId] = false;

    this.setQuestionOrderNumbers();
  }

  /**
   * Set the order numbers of the questions according to their order in the UI.
   */
  private setQuestionOrderNumbers() {
    for (let i = 0; i < this.participantQuestions.length; i++) {
      this.participantQuestions[i].order_number = i + 1;
    }
    for (let i = 0; i < this.bookingCustomerQuestions.length; i++) {
      this.bookingCustomerQuestions[i].order_number = i + 1;
    }
  }

  /**
   * Save the given question and its set of time slots using the API.
   *
   * @return A promise with resolves if the question was saved correctly and
   *   rejects if not.
   */
  public saveQuestion(question: EventQuestion): Promise<void> {
    this.eventQuestionsErrorMessage[question.id] = '';
    this.eventQuestionsErrors[question.id] = {};

    const oldQuestionId = question.id;
    // Used in the finally block to remove the loading state.
    let newQuestionId = null;

    this.loadingEventQuestions[question.id] = true;

    let questionSubmitPromise: Promise<EventQuestion>;
    if (question.id < 0) {
      questionSubmitPromise = this.eventTypeService
        .addEventQuestion(this.eventType.id, question);
    } else {
      questionSubmitPromise = this.eventTypeService
        .updateEventQuestion(question);
    }

    return questionSubmitPromise
      .then(updatedQuestion => {
        newQuestionId = updatedQuestion.id;

        // Replace the old question with the updated question.
        const questions = this.getQuestionListForScope(updatedQuestion.scope);
        questions.splice(questions.indexOf(question), 1, updatedQuestion);
        if (updatedQuestion.id !== oldQuestionId) {
          this.questionInEditMode[updatedQuestion.id] = this.questionInEditMode[oldQuestionId];
          this.loadingEventQuestions[updatedQuestion.id] = this.loadingEventQuestions[oldQuestionId];
          delete this.questionInEditMode[oldQuestionId];
          delete this.loadingEventQuestions[oldQuestionId];
        }

        this.questionInEditMode[updatedQuestion.id] = false;
      })
      .catch(reason => {
        if (reason.status === 400) {
          this.eventQuestionsErrors[question.id] = reason.error;
          this.eventQuestionsErrorMessage[question.id] = 'Please correct the errors above and try again.';
        } else if (reason.status === 403) {
          if (question.id < 0) {
            this.eventQuestionsErrorMessage[question.id] = 'You do not have permission to add questions.';
          } else {
            this.eventQuestionsErrorMessage[question.id] = 'You do not have permission to modify this question.';
          }
        } else {
          this.serverError = reason.status;
        }

        return Promise.reject();
      })
      .finally(() => {
        if (newQuestionId !== null) {
          this.loadingEventQuestions[newQuestionId] = false;
        } else {
          this.loadingEventQuestions[oldQuestionId] = false;
        }
      });
  }

  /**
   * Remove the given question.
   *
   * Note that the API removes the question's time slots automatically when the
   * question is deleted.
   */
  public removeQuestion(question: EventQuestion) {
    // If the question has a positive ID, then it is present in the API and must
    // be deleted.
    if (question.id > 0) {
      this.eventTypeService.deleteEventQuestion(question);
    }

    delete this.questionInEditMode[question.id];
    delete this.loadingEventQuestions[question.id];

    const questions = this.getQuestionListForScope(question.scope);

    const questionIdx = questions.indexOf(question);

    questions.splice(questionIdx, 1);

    this.setQuestionOrderNumbers();

    // For any questions after the removed one which have previously been saved
    // to the API, we will make patch requests to the API to update their order
    // numbers.
    for (let i = questionIdx; i < questions.length; i++) {
      if (questions[i].id > 0) {
        this.eventTypeService.setEventQuestionOrderNumber(questions[i]);
      }
    }
  }

  /**
   * Save all questions in edit mode and navigate to the next page.
   */
  public saveAll() {
    const saveQuestionPromises: Promise<void>[] = [];

    // Save any questions in edit mode.
    for (const question of this.participantQuestions.concat(this.bookingCustomerQuestions)) {
      if (this.questionInEditMode[question.id]) {
        saveQuestionPromises.push(
          this.saveQuestion(question)
        );
      }
    }

    // Only navigate to the new page if all outstanding questions saved successfully.
    Promise.all(saveQuestionPromises)
      .then(() => {
        // Routing depends on which save button was clicked.
        if (this.submitType === this.SUBMIT_TYPE_GO_BACK) {
          this.router.navigate(
            [
              '/events/setup/event-types'
            ],
            {
              queryParams: {
                status: this.eventType.status
              },
              state: {
                feedback: {
                  text: 'Event \'' + this.eventType.name + '\' saved successfully.',
                  status: 'success'
                }
              }
            }
          );
        } else if (this.submitType === this.SUBMIT_TYPE_CONTINUE) {
          this.router.navigate(
            ['/events/setup/edit/' + this.eventType.id.toString() + '/columns']
          );
        }
      });
  }

  /**
   * Whether the given question requires options to be provided.
   */
  public questionHasOptions(question: EventQuestion) {
    return [
      EventQuestionType.multipleChoice,
      EventQuestionType.multipleCheckboxes
    ].includes(question.question_type);
  }

  /**
   * Move the given question one spot up or down in the questions list.
   */
  public shiftQuestion(question: EventQuestion, shiftUp: boolean) {
    const questions = this.getQuestionListForScope(question.scope);

    const questionIdx = questions.indexOf(question);

    if (shiftUp) {
      questions[questionIdx] = questions[questionIdx - 1];
      questions[questionIdx - 1] = question;
    } else {
      questions[questionIdx] = questions[questionIdx + 1];
      questions[questionIdx + 1] = question;
    }

    this.setQuestionOrderNumbers();

    // If the moved question has previously been saved to the API, we will make
    // a PATCH request to the API to update its order number.
    if (question.id > 0) {
      this.eventTypeService.setEventQuestionOrderNumber(question);
    }

    // If the swapped question which was moved is not in edit mode, we must make
    // a PATCH request to the API to update its order number.
    const swappedQuestion = questions[questionIdx];
    if (!this.questionInEditMode[swappedQuestion.id]) {
      this.eventTypeService.setEventQuestionOrderNumber(swappedQuestion);
    }
  }

  public getRenderedInlineMarkdown(content: string) {
    return this.domSanitizer.bypassSecurityTrustHtml(
      marked.parseInline(
        content,
        {
          breaks: true
        }
      )
    );
  }

  public getRenderedMarkdown(content: string) {
    return this.domSanitizer.bypassSecurityTrustHtml(
      marked(
        content,
        {
          breaks: true
        }
      )
    );
  }

  public getUnusedCustomCustomerFields(editedQuestion: EventQuestion) {
    const questions = this.getQuestionListForScope(editedQuestion.scope);

    const usedFieldIds: number[] = [];
    for (const question of questions) {
      if ((question.custom_customer_field_id !== null) && (question.id !== editedQuestion.id)) {
        usedFieldIds.push(question.custom_customer_field_id);
      }
    }

    return this.customCustomerFields.filter(ccf => !usedFieldIds.includes(ccf.id));
  }

  public isPreferredNameSelected(scope: EventQuestionScope): boolean {
    return this
      .getQuestionListForScope(scope)
      .some(question => question.question_type === EventQuestionType.preferredName);
  }

  /**
   * Load the event type with the given ID.
   *
   * @param eventTypeId - The API ID of the event type.
   */
  private loadEventType(eventTypeId: number) {
    this.loadingEventType = true;

    this.eventTypeService
      .getEventType(eventTypeId)
      .then(eventType => {
        this.eventType = eventType;
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else if (reason.status === 404) {
          this.eventTypeNotFound = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingEventType = false; });
  }

  /**
   * Load the event questions with the given event type ID.
   *
   * @param eventTypeId - The API ID of the event type.
   */
  private loadEventQuestions(eventTypeId: number) {
    this.loadingEventQuestionsList = true;

    // TODO: Support more than 100 questions.
    this.eventTypeService
      .getEventQuestions(eventTypeId, 1, 100)
      .then(questionResponse => {
        this.participantQuestions = questionResponse.results
          .filter(q => q.scope === EventQuestionScope.participant);
        this.bookingCustomerQuestions = questionResponse.results
          .filter(q => q.scope === EventQuestionScope.bookingCustomer);

        for (const es of questionResponse.results) {
          this.questionInEditMode[es.id] = false;
        }
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else if (reason.status === 404) {
          this.eventQuestionsNotFound = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingEventQuestionsList = false; });
  }

  private loadCustomCustomerFields() {
    this.loadingCustomCustomerFields = true;

    this.orgConfigService
      .getCustomCustomerFields(1, 3000)
      .then(result => { this.customCustomerFields = result.results; })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingCustomCustomerFields = false; });
  }

  private getQuestionListForScope(scope: EventQuestionScope) {
    switch (scope) {
      case EventQuestionScope.participant:
        return this.participantQuestions;
      case EventQuestionScope.bookingCustomer:
        return this.bookingCustomerQuestions;
    }
  }
}
